import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Target, Plus, TrendingUp, CheckCircle2, ArrowLeft, Loader2, PartyPopper
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WellnessGoals({ onBack, clientId }) {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: '', description: '', target_value: '', unit: '', target_date: '' });
  const [updateValues, setUpdateValues] = useState({});
  const [celebrating, setCelebrating] = useState(null);
  const canCreate = ['agency_admin', 'ceo', 'caregiver'].includes(user?.role);

  useEffect(() => { fetchGoals(); }, [clientId]);

  const fetchGoals = async () => {
    try {
      const url = clientId
        ? `${API_URL}/api/wellness-goals?client_id=${clientId}`
        : `${API_URL}/api/wellness-goals`;
      const { data } = await axios.get(url, { withCredentials: true });
      setGoals(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async () => {
    if (!newGoal.title) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/wellness-goals`, {
        client_id: clientId || user?.id,
        title: newGoal.title,
        description: newGoal.description,
        target_value: newGoal.target_value ? parseFloat(newGoal.target_value) : null,
        unit: newGoal.unit || null,
        target_date: newGoal.target_date || null
      }, { withCredentials: true });
      toast.success('Goal created!');
      setShowCreate(false);
      setNewGoal({ title: '', description: '', target_value: '', unit: '', target_date: '' });
      fetchGoals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create goal');
    } finally {
      setCreating(false);
    }
  };

  const updateProgress = async (goalId) => {
    const val = parseFloat(updateValues[goalId]);
    if (isNaN(val)) { toast.error('Enter a valid number'); return; }
    try {
      const { data } = await axios.put(`${API_URL}/api/wellness-goals/${goalId}`,
        { current_value: val }, { withCredentials: true });
      if (data.status === 'completed') {
        setCelebrating(goalId);
        toast.success('Goal completed! Celebration time!');
        setTimeout(() => setCelebrating(null), 3000);
      } else {
        toast.success('Progress updated!');
      }
      setUpdateValues(prev => ({ ...prev, [goalId]: '' }));
      fetchGoals();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const getProgress = (goal) => {
    if (!goal.target_value) return 0;
    return Math.min(Math.round((goal.current_value / goal.target_value) * 100), 100);
  };

  return (
    <div className="p-6 space-y-6" data-testid="wellness-goals-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Wellness Goals</h2>
            <p className="text-sm text-muted-foreground">Track health and wellness milestones</p>
          </div>
        </div>
        {canCreate && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-goal-btn">
                <Plus className="w-4 h-4 mr-2" />New Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-border" aria-describedby="create-goal-description">
              <DialogHeader>
                <DialogTitle className="text-foreground">Create Wellness Goal</DialogTitle>
              </DialogHeader>
              <p id="create-goal-description" className="sr-only">Form to create a new wellness goal for a client</p>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Goal Title</Label>
                  <Input value={newGoal.title}
                    onChange={(e) => setNewGoal(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g., Walk 10,000 steps daily"
                    className="bg-muted border-border text-foreground"
                    data-testid="goal-title-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Description</Label>
                  <Input value={newGoal.description}
                    onChange={(e) => setNewGoal(p => ({ ...p, description: e.target.value }))}
                    placeholder="Details about this goal"
                    className="bg-muted border-border text-foreground" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-foreground">Target Value</Label>
                    <Input type="number" value={newGoal.target_value}
                      onChange={(e) => setNewGoal(p => ({ ...p, target_value: e.target.value }))}
                      placeholder="e.g., 10000"
                      className="bg-muted border-border text-foreground"
                      data-testid="goal-target-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Unit</Label>
                    <Input value={newGoal.unit}
                      onChange={(e) => setNewGoal(p => ({ ...p, unit: e.target.value }))}
                      placeholder="steps, lbs, etc."
                      className="bg-muted border-border text-foreground" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Target Date</Label>
                  <Input type="date" value={newGoal.target_date}
                    onChange={(e) => setNewGoal(p => ({ ...p, target_date: e.target.value }))}
                    className="bg-muted border-border text-foreground" />
                </div>
                <Button className="btn-primary w-full" onClick={createGoal} disabled={creating}
                  data-testid="save-goal-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Goal'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : goals.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No wellness goals set yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((goal) => {
            const progress = getProgress(goal);
            const isCelebrating = celebrating === goal.id;
            return (
              <Card key={goal.id} className={`glass-card border-border transition-all
                ${isCelebrating ? 'ring-2 ring-[#FF6F61] animate-pulse' : ''}`}
                data-testid={`goal-card-${goal.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base text-foreground flex items-center gap-2">
                      {goal.status === 'completed' ? (
                        <PartyPopper className="w-4 h-4 text-[#FF6F61]" />
                      ) : (
                        <Target className="w-4 h-4 text-[#A8E6CF]" />
                      )}
                      {goal.title}
                    </CardTitle>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      goal.status === 'completed' ? 'bg-[#A8E6CF]/20 text-[#A8E6CF]' :
                      goal.status === 'paused' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {goal.status?.replace('_', ' ')}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {goal.description && <p className="text-xs text-muted-foreground mb-3">{goal.description}</p>}

                  {goal.target_value && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="text-foreground">
                          {goal.current_value} / {goal.target_value} {goal.unit || ''}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${progress}%`,
                            backgroundColor: progress >= 100 ? '#A8E6CF' : progress > 50 ? '#FF6F61' : '#4A90E2'
                          }} />
                      </div>
                      <p className="text-right text-xs text-muted-foreground mt-1">{progress}%</p>
                    </div>
                  )}

                  {goal.target_date && (
                    <p className="text-xs text-muted-foreground mb-3">Target: {goal.target_date}</p>
                  )}

                  {goal.status !== 'completed' && canCreate && (
                    <div className="flex items-center gap-2 mt-2">
                      <Input type="number" placeholder="Update value"
                        value={updateValues[goal.id] || ''}
                        onChange={(e) => setUpdateValues(p => ({ ...p, [goal.id]: e.target.value }))}
                        className="bg-muted border-border text-foreground h-8 text-sm"
                        data-testid={`update-input-${goal.id}`} />
                      <Button size="sm" variant="outline" className="border-border text-muted-foreground h-8"
                        onClick={() => updateProgress(goal.id)}
                        data-testid={`update-btn-${goal.id}`}>
                        <TrendingUp className="w-3 h-3" />
                      </Button>
                    </div>
                  )}

                  {isCelebrating && (
                    <div className="mt-3 p-3 rounded-lg bg-[#FF6F61]/10 border border-[#FF6F61]/30 text-center">
                      <p className="text-[#FF6F61] font-medium text-sm">Goal Achieved!</p>
                      <p className="text-xs text-muted-foreground">Amazing progress!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
